/**
 * gameState.ts
 * Module-level mutable game state store for the Starfall Courier game loop.
 *
 * This is a plain mutable object — not React state, not Redux.
 * The game loop writes to it directly every tick; rendering reads from it.
 * GameCanvas reads this directly (it is the sole React/engine boundary).
 * Other React components should not import this.
 *
 * Phase 1 — Movement Prototype.
 * See ROADMAP.md Phase 1 and ADR-003.
 *
 * DECISION: Type/interface definitions live in `gameStateTypes.ts` so this
 * file stays within the AGENTS.md 300-line cap. Re-exported here so the
 * existing `from '../engine/gameState'` import surface keeps working.
 *
 * @module gameState
 */

import { NOVA_MAX_HP, NOVA_LIVES_START } from '../../constants/gameConstants';
import type {
  GameState,
  LevelRuntime,
} from './gameStateTypes';

export type {
  CollectibleKind,
  CollectibleRuntime,
  WeaponPickupKind,
  WeaponPickupRuntime,
  Enemy,
  NovaAnimState,
  NovaRuntime,
  CameraRuntime,
  LevelRuntime,
  GameState,
} from './gameStateTypes';


/**
 * The single mutable game state instance.
 * Initialised with safe zero values; call `initGameState()` before the first tick.
 */
export const gameState: GameState = {
  nova: {
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    isGrounded: false,
    coyoteFrames: 0,
    jumpBufferFrames: 0,
    isJumping: false,
    state: 'IDLE',
    invincibilityFrames: 0,
    health: NOVA_MAX_HP,
    maxHealth: NOVA_MAX_HP,
    facingRight: true,
    isStomping: false,
    hurtFlash: false,
    animTransitionFrames: 0,
    prevGrounded: true,
    canDoubleJump: false,
    airJumpsRemaining: 0,
    canWallSlide: false,
    canDash: false,
    isCrouching: false,
    isWallSliding: false,
    dashFramesRemaining: 0,
    dashCooldownFrames: 0,
    dashDirection: 1,
    airDashUsed: false,
  },
  camera: { x: 0, y: 0 },
  level: { width: 0, height: 0, platforms: [], oneWayPlatforms: [], exitPortalActive: false },
  spawnX: 0,
  spawnY: 0,
  checkpointSpawnX: 0,
  checkpointSpawnY: 0,
  elapsedSeconds: 0,
  isRunning: false,
  seeds: 0,
  shardsCollected: 0,
  shardsTotal: 0,
  score: 0,
  heartFragments: 0,
  lives: NOVA_LIVES_START,
  projectiles: [],
  attackCooldown: 0,
  projectileKind: 'star_shard',
  ammo: 0,
  enemies: [],
  collectibles: [],
  weaponPickups: [],
  levelComplete: false,
  bossDefeated: false,
  finaleActive: false,
  gameOver: false,
  suppressPlayerFire: false,
};

/**
 * Reset the game state to the provided spawn position and level geometry.
 * Call this once when loading a level, before starting the game loop.
 * @param spawnX - Nova's starting X position in world space (logical px).
 * @param spawnY - Nova's starting Y position in world space (logical px).
 * @param level  - Level geometry to load into the runtime state.
 * @param health - Starting health value (defaults to NOVA_MAX_HP).
 */
export function initGameState(
  spawnX: number,
  spawnY: number,
  level: LevelRuntime,
  health = NOVA_MAX_HP,
): void {
  gameState.nova.x = spawnX;
  gameState.nova.y = spawnY;
  gameState.nova.vx = 0;
  gameState.nova.vy = 0;
  gameState.nova.isGrounded = false;
  gameState.nova.coyoteFrames = 0;
  gameState.nova.jumpBufferFrames = 0;
  gameState.nova.isJumping = false;
  gameState.nova.state = 'IDLE';
  gameState.nova.invincibilityFrames = 0;
  gameState.nova.health = health;
  gameState.nova.maxHealth = NOVA_MAX_HP;
  gameState.nova.facingRight = true;
  gameState.nova.isStomping = false;
  gameState.nova.hurtFlash = false;
  gameState.nova.animTransitionFrames = 0;
  gameState.nova.prevGrounded = true;
  // DECISION: Preserve `canDoubleJump` across init (session-scope ability);
  // reset the bank per level. Flag wiring deferred to World 3 unlock — see field JSDoc.
  gameState.nova.airJumpsRemaining = 0;
  // DECISION: same split for the newer abilities — the unlock flags
  // (`canWallSlide`, `canDash`) are session-scope and survive init, but every
  // transient below is per-level. Without this a dash in progress when a level
  // ends would resume mid-air on the next one, and a crouch held through a
  // transition would spawn Nova with a shrunk collider.
  gameState.nova.isCrouching = false;
  gameState.nova.isWallSliding = false;
  gameState.nova.dashFramesRemaining = 0;
  gameState.nova.dashCooldownFrames = 0;
  gameState.nova.dashDirection = 1;
  gameState.nova.airDashUsed = false;
  gameState.camera.x = 0;
  gameState.camera.y = 0;
  gameState.level = level;
  gameState.spawnX = spawnX;
  gameState.spawnY = spawnY;
  gameState.checkpointSpawnX = spawnX;
  gameState.checkpointSpawnY = spawnY;
  gameState.elapsedSeconds = 0;
  gameState.isRunning = true;
  gameState.seeds = 0;
  gameState.shardsCollected = 0;
  gameState.shardsTotal = 0;
  gameState.score = 0;
  gameState.heartFragments = 0;
  gameState.lives = NOVA_LIVES_START;
  gameState.projectiles = [];
  gameState.attackCooldown = 0;
  // DECISION: Reset to the canonical default. Without this, a prior session
  // (or test) that flipped projectileKind to 'gem_shard' would leak into the
  // next initGameState() caller; PlayScene.init() seeds from the dev toggle
  // immediately after this call, but other init paths (unit tests, future
  // scenes) need the safe baseline.
  gameState.projectileKind = 'star_shard';
  // DECISION: Reset ammo + weapon pickup list with the same baseline rule —
  // a prior session’s mid-run weapon must never leak into the next init.
  gameState.ammo = 0;
  gameState.enemies = [];
  gameState.collectibles = [];
  gameState.weaponPickups = [];
  gameState.levelComplete = false;
  gameState.bossDefeated = false;
  gameState.finaleActive = false;
  gameState.gameOver = false;
  gameState.suppressPlayerFire = false;
}
